"use client";

import { useId, useState } from "react";
import Image from "next/image";
import Link from "next/link";

import { useLanguage } from "../i18n/LanguageProvider";
import { SERVICES_BERANDA, layananHref } from "../data/layanan";

/* ============================================================
   SERVICE ACCORDION - BERANDA

   MENAMPILKAN TUJUH LAYANAN, BUKAN LIMA BELAS.

   Yang tujuh ditentukan di app/data/layanan.js lewat
   JUMLAH_DI_BERANDA, bukan di sini. Ubah angkanya di sana kalau perlu.

   Kenapa tidak semuanya: akordeon ini dibuat untuk MERINGKAS - versi
   sebelumnya delapan kartu yang memakan hampir satu layar penuh untuk
   menyampaikan delapan nama. Lima belas baris mengembalikan masalah
   yang sama.

   Kelima belasnya ada di /layanan, yang dituju tombol "Lihat semua
   layanan" di kepala section (dirender oleh app/page.jsx).

   ============================================================
   TAUTAN "SELENGKAPNYA" HANYA MUNCUL KALAU HALAMANNYA ADA

   Sebelas dari lima belas layanan belum punya app/layanan/{slug}/.
   Menautkannya berarti mengirim pengunjung ke 404 - lebih buruk
   daripada tidak menawarkan tautan sama sekali, karena pengunjung
   menyimpulkan situsnya rusak, bukan bahwa halamannya belum ada.

   Penandanya `siap` di app/data/layanan.js. Deskripsinya tetap tampil
   untuk semua; hanya tautannya yang ditahan.

   ============================================================
   >>> JANGAN TULIS className SEBAGAI STRING MULTI-BARIS. <<<
   Proyek ini berakhiran baris CRLF. String class yang dipecah beberapa
   baris ikut membawa \r\n ke dalam nilai atribut; server dan klien
   menormalkannya berbeda, dan React melaporkannya sebagai hydration
   error. Kalau perlu dipecah, pakai ["...", "..."].join(" ").
   ============================================================ */

export default function ServiceAccordion() {
  const { t } = useLanguage();

  /* Item pertama terbuka sejak awal. Akordeon yang seluruhnya tertutup
     saat halaman dimuat terlihat seperti daftar mati - pengunjung tidak
     langsung tahu bahwa barisnya bisa dibuka. */
  const [openIndex, setOpenIndex] = useState(0);

  /* Indeks TERPISAH untuk panel kanan. Nilainya tidak pernah -1: ia
     hanya berubah saat sebuah item dibuka, dan bertahan saat semuanya
     ditutup. Inilah yang menghentikan panel berkedip balik ke item
     pertama. */
  const [panelIndex, setPanelIndex] = useState(0);
  const baseId = useId();

  const active = SERVICES_BERANDA[panelIndex];
  const ActiveIcon = active.Icon;

  const toggle = (i) => {
    setOpenIndex((current) => (current === i ? -1 : i));
    setPanelIndex(i);
  };

  return (
    <div className="grid gap-8 lg:grid-cols-[1.7fr_1fr] lg:gap-16">

      <ul className="border-t border-[var(--c-line)]">
        {SERVICES_BERANDA.map((service, i) => {
          const isOpen = i === openIndex;
          const panelId = `${baseId}-panel-${i}`;
          const buttonId = `${baseId}-button-${i}`;
          const Icon = service.Icon;

          return (
            <li key={service.key} className="border-b border-[var(--c-line)]">
              {/* aria-expanded dan aria-controls menghubungkan tombol
                 dengan panelnya. Tanpa keduanya, pembaca layar hanya
                 mendengar tombol tanpa tahu ia membuka apa, atau apakah
                 isinya sedang terbuka. */}
              <button
                type="button"
                id={buttonId}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggle(i)}
                className="flex w-full items-center gap-3 py-3.5 text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--c-brand-mid)] md:gap-4 md:py-5"
              >
                {/* Dudukan ikon disalin dari dropdown Header: kotak
                   #f1faf5 dengan garis dalam #def2e8 dan ikon #15804a,
                   diperbesar karena barisnya lebih lapang. */}
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset transition-colors duration-300 motion-reduce:transition-none md:h-11 md:w-11 ${isOpen ? "bg-[var(--c-tint-2)] ring-[var(--c-brand-300)]" : "bg-[var(--c-tint)] ring-[var(--c-brand-line-2)]"}`}>
                  <Icon size={18} className="text-[var(--c-brand-mid)] md:h-[22px] md:w-[22px]" aria-hidden="true" />
                </span>

                <span className={`flex-1 text-[15px] font-bold leading-snug transition-colors md:text-lg ${isOpen ? "text-[var(--c-brand)]" : "text-[var(--c-ink)]"}`}>
                  {t(`header.layanan.${service.key}`)}
                </span>

                {service.urgent && (
                  <span className="hidden shrink-0 rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-900 sm:inline">
                    {t("layananList.darurat")}
                  </span>
                )}

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                  className={`h-5 w-5 shrink-0 text-[var(--c-faint)] transition-transform duration-300 motion-reduce:transition-none md:h-6 md:w-6 ${isOpen ? "rotate-180 text-[var(--c-brand-mid)]" : ""}`}
                >
                  <path
                    d="m6 9 6 6 6-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Panel dibuka dengan grid-template-rows 0fr -> 1fr.

                 Ini cara menganimasikan tinggi otomatis tanpa mengukur
                 lewat JavaScript. max-height yang sering dipakai orang
                 mengharuskan menebak angka: kekecilan memotong isi,
                 kebesaran membuat animasinya terasa lambat di awal. */}
              <div
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                className={`grid transition-all duration-300 ease-out motion-reduce:transition-none ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
              >
                <div className="overflow-hidden">
                  <div className="pb-4 pl-12 pr-2 md:pb-6 md:pl-15">
                    <p className="text-sm leading-relaxed text-[var(--c-body)] md:text-base">
                      {t(`layananList.${service.key}Desc`)}
                    </p>

                    {/* tabIndex -1 saat panel tertutup. Panel memakai
                       grid-rows-[0fr] dan bukan display:none, jadi tanpa
                       ini pengguna keyboard akan Tab ke tautan yang
                       tidak terlihat. */}
                    {service.siap && (
                      <Link
                        href={layananHref(service.slug)}
                        tabIndex={isOpen ? 0 : -1}
                        className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[var(--c-brand)] underline-offset-4 transition hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)] md:text-base"
                      >
                        {t("umum.selengkapnya")}
                        <span aria-hidden="true">&rarr;</span>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* PANEL KANAN
         Disembunyikan di bawah lg: pada layar sempit ia terdorong ke
         bawah daftar dan berubah jadi gambar besar yang tidak
         menjelaskan apa-apa.

         sticky top-32 supaya tetap terlihat saat daftar digulir. Angka
         32 (128px) kira-kira setinggi header fixed plus jarak.

         aria-hidden: seluruh isinya, nama layanan dan deskripsinya,
         sudah dibacakan di dalam panel akordeon di sebelah kiri. Tanpa
         ini pembaca layar mengucapkan isi yang sama dua kali. */}
      <div className="hidden lg:block" aria-hidden="true">
        <div className="sticky top-32">
          <div className="overflow-hidden rounded-2xl bg-[var(--c-surface-2)] ring-1 ring-[var(--c-line)]">
            <div className="relative flex aspect-[4/3] items-center justify-center bg-gradient-to-br from-[var(--c-tint)] to-[var(--c-line-soft)]">
              {active.image ? (
                <Image
                  src={active.image}
                  fill
                  sizes="(min-width: 1280px) 460px, 380px"
                  alt=""
                  className="object-cover"
                />
              ) : (
                <ActiveIcon size={96} strokeWidth={1.25} className="text-[var(--c-brand-mid)] opacity-70" />
              )}
            </div>

            <div className="p-5 md:p-6">
              <p className="mb-2 text-lg font-bold text-[var(--c-ink)] md:text-xl">
                {t(`header.layanan.${active.key}`)}
              </p>
              <p className="text-sm leading-relaxed text-[var(--c-body)]">
                {t(`layananList.${active.key}Desc`)}
              </p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
